import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

import { FacilityGetModel } from "./Facilities";

/**
 * ### 他事業所 受入リクエスト API 操作クラス
 */
export default class OtherOfficeAcceptanceRequestReplies {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET OtherOfficeAcceptanceRequestReplies
   */
  public async gets(
    userStore: LoginUser,
    datetime: number,
    queryParams?: { startIndex?: number; endIndex?: number; senderFacilityId?: number }
  ): Promise<Array<OtherOfficeAcceptanceRequestRepliesGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      datetime: datetime
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeAcceptanceRequestReplies, formatParams);
    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<OtherOfficeAcceptanceRequestRepliesGetModel>>(
      userStore,
      requestUrl,
      this.constructor.name,
      params,
      HitomeqErrorType.REQUEST_RETRY,
      0
    );

    return response.data;
  }

  /**
   * ### GET OtherOfficeAcceptanceRequestReplies
   */
  public async getCount(userStore: LoginUser, datetime: number): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      datetime: datetime
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeAcceptanceRequestReplies, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * ### POST OtherOfficeAcceptanceRequestReplies
   */
  public async post(userStore: LoginUser, requestBody: OtherOfficeAcceptanceRequestRepliesPostModel): Promise<OtherOfficeAcceptanceRequestRepliesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeAcceptanceRequestReply, formatParams);
    const response = await this.commonService.executePost<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### PATCH OtherOfficeAcceptanceRequestReplies
   */
  public async patch(userStore: LoginUser, datetime: number, requestBody: OtherOfficeAcceptanceRequestRepliesPatchModel): Promise<OtherOfficeAcceptanceRequestRepliesGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      datetime: datetime
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.OtherOfficeAcceptanceRequestReplies, formatParams);
    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface OtherOfficeAcceptanceRequestRepliesGetModel {
  /** 送信した施設 */
  facility: FacilityGetModel;

  /** 1件目の返信 */
  firstReply: OtherOfficeAcceptanceRequestReply | null;
  /** 2件目以降の返信 */
  followUpReplies: Array<OtherOfficeAcceptanceRequestReply>;
  /** 最後の返信の受け入れ可否 */
  latestAcceptance: Acceptance | null;
}

export interface OtherOfficeAcceptanceRequestReply {
  /** パーティションキー（{facilityId}_{datetime}） */
  parentKey: number;
  /** ソートキー（{parentKey.facilityId}-{senderFacilityId}_{datetime}） */
  sortKey: string;

  /** スレッドID（{parentKey.facilityId}-{senderFacilityId}） */
  threadId: string;
  /** リクエスト日時 */
  datetime: number;
  /** 返信した施設ID */
  senderFacilityId: number;

  /** 受け入れ可否 */
  acceptance: Acceptance | null;
  /** メッセージ内容（補足説明等） */
  messageText: string;

  /** リクエストしたユーザーID */
  userId: string;
  /** 削除フラグ */
  deleteFlag: boolean;
  /** 作成日時 */
  created: number;
  /** 更新日時 */
  updated: number;
}

export enum Acceptance {
  /** 受け入れ可 */
  POSSIBLE = "possible",
  /** 詳細相談希望 */
  NEED_CONSULTATION = "needConsultation",
  /** 受け入れ不可 */
  NOT_POSSIBLE = "notPossible"
}

export interface OtherOfficeAcceptanceRequestRepliesPostModel {
  /** 更新元パーティションキー */
  parentKey: string;
  /** 受け入れ可否 */
  acceptance: Acceptance | null;
  /** 返信内容 */
  messageText: string;

  /** 返信先施設ID。自分が送信したリクエストに対する返信の場合に格納される */
  receiverFacilityId?: number;
}

export interface OtherOfficeAcceptanceRequestRepliesPatchModel {
  /** 返信元リクエストのパーティションキー */
  parentKey: string;
  /** 受け入れ可否 */
  acceptance: Acceptance | null;
  /** 返信内容 */
  messageText: string;

  /** 競合検知用の更新日時 */
  updated: number;
}
